import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
    getVendorSupportTicketsRequest,
    getVendorTicketStatusRequest,
    getVendorTicketStoresRequest,
    getVendorTicketConversationRequest,
    getVendorTicketCommentsRequest,
    shareVendorTicketRequest,
    updateVendorTicketRequest,
} from "../../../redux/vendor/supportTickets/action";
import {
    VendorChevronDownSvg,
    VendorFilterSvg,
    VendorSearchSvg,
    VendorEmptyMailboxSvg,
} from "../../../components/CommonSvg";
import logoDark from "../../../assets/images/logo-dark.webp";
import { PER_PAGE, longStamp, toView } from "./ticketUtils";
import TicketFilters from "./TicketFilters";
import TicketRow from "./TicketRow";
import TicketPager from "./TicketPager";
import TicketDetailHead from "./TicketDetailHead";
import ForwardCompose from "./ForwardCompose";
import ConversationMessage from "./ConversationMessage";
import ActivitiesModal from "./modals/ActivitiesModal";
import StatusChangeModal from "./modals/StatusChangeModal";
import CustomisedModal from "../../../components/Modal/CustomisedModal";
import { vendorHasRole } from "../../../utils/authUtils";
import {
    DANGER,
    SUCCESS,
    VendorSharedTicketAccess,
    VendorUpdateTicketStatusAccess,
} from "../../../constant/commonConstants";
import { ApiResponseMessage } from "../../../helper/tosterHelper";

const VendorSupportTickets = ({
    ticketList,
    vendors,
    totalRecords,
    totalPages,
    statusList,
    storeList,
    loading,
    getVendorSupportTickets,
    getVendorTicketStatus,
    getVendorTicketStores,
    getVendorTicketConversation,
    conversation,
    conversationLoading,
    getVendorTicketComments,
    ticketComments,
    ticketCommentsLoading,
    shareVendorTicket,
    sharing,
    shareResult,
    updateVendorTicket,
    updating,
    updateResult,
}) => {
    /* The dashboard links straight at one ticket. There is no endpoint for a
       single ticket, so it is fetched the only way there is - one narrow list
       call for that ticket no. - and then kept aside in linkedTicket while the
       normal list loads behind it. The search box stays empty and the list
       stays whole: the link opens a ticket, it does not filter the inbox. */
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedTicketNo = searchParams.get("ticketNo") || "";
    const linkedTicketId = searchParams.get("ticketId") || "";
    const [pendingLink, setPendingLink] = useState(
        linkedTicketNo ? { no: linkedTicketNo, id: linkedTicketId } : null
    );
    const [fetchingLink, setFetchingLink] = useState(Boolean(linkedTicketNo));
    const [linkedTicket, setLinkedTicket] = useState(null);
    const fetchSeen = useRef(false);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [vendorZohoId, setVendorZohoId] = useState("");
    const [category, setCategory] = useState("");
    const [priority, setPriority] = useState("");
    const [hraMemberId, setHraMemberId] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [selectedKey, setSelectedKey] = useState(null);
    const [composing, setComposing] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    /* the status the user picked in the dropdown, held until they confirm it in
       the modal - the ticket itself is not touched before that */
    const [pendingStatus, setPendingStatus] = useState("");
    /* Reply opens the very same modal, just without a status already chosen
       for it - the dropdown inside starts on the ticket's own status */
    const [replying, setReplying] = useState(false);

    const canShareTickets = vendorHasRole(VendorSharedTicketAccess);
    const canUpdateTickets = vendorHasRole(VendorUpdateTicketStatusAccess);

    const [viewing, setViewing] = useState(false);

    /* every message from this screen goes to the app's toast in the top right -
       the same one the rest of the portal answers with. It used to be a bar at
       the bottom of the page, which put ticket errors somewhere the user was
       not looking. */
    const showToast = (message, kind = DANGER) => {
        if (!message) return;
        ApiResponseMessage(message, kind);
    };

    useEffect(() => {
        document.body.classList.add("vp-tk-locked");
        return () => document.body.classList.remove("vp-tk-locked");
    }, []);

    useEffect(() => {
        getVendorTicketStatus();
        getVendorTicketStores();
    }, []);

    const rangeApplied = Boolean(startDate && endDate);

    const loadTickets = () => {
        getVendorSupportTickets({
            searchText: search,
            status: status,
            vendorZohoId: vendorZohoId,
            category: category,
            priority: priority,
            hraMemberId: hraMemberId,
            startDate: rangeApplied ? moment(startDate).format("MM-DD-YYYY") : "",
            endDate: rangeApplied ? moment(endDate).format("MM-DD-YYYY") : "",
            pagination: { page: page, limit: PER_PAGE },
        });
    };

    useEffect(() => {
        /* the linked ticket is fetched first and on its own - the full list
           follows as soon as it has been picked out of the answer */
        if (fetchingLink) {
            getVendorSupportTickets({
                searchText: pendingLink?.no || "",
                pagination: { page: 1, limit: PER_PAGE },
            });
            return;
        }
        loadTickets();
    }, [
        fetchingLink,
        page,
        search,
        status,
        vendorZohoId,
        category,
        priority,
        hraMemberId,
        rangeApplied,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        setPage(1);
    }, [
        search,
        status,
        vendorZohoId,
        category,
        priority,
        hraMemberId,
        rangeApplied,
    ]);

    const activeFilterCount = [
        status,
        vendorZohoId,
        category,
        priority,
        hraMemberId,
        rangeApplied ? "date" : "",
    ].filter(Boolean).length;

    const clearFilters = () => {
        setStatus("");
        setVendorZohoId("");
        setCategory("");
        setPriority("");
        setHraMemberId("");
        setStartDate(null);
        setEndDate(null);
    };

    /* a vendor user can be given access to several vendors - when they are,
       the list mixes tickets from all of them, so each row has to say which
       one it came from and the filter is worth offering */
    const hasMultipleVendors = (vendors || []).length > 1;

    const vendorOptions = useMemo(
        () =>
            (vendors || [])
                .filter((row) => row.vendorZohoId)
                .map((row) => ({ value: String(row.vendorZohoId), label: row.name })),
        [vendors]
    );

    const storeOptions = useMemo(
        () =>
            (storeList || []).map((row) => ({
                value: row.hraMemberId,
                label: row.store ? `${row.store} - ${row.hraMemberId}` : row.hraMemberId,
            })),
        [storeList]
    );

    const statusOptions = useMemo(
        () => (statusList || []).map((row) => ({ value: row.name, label: row.name })),
        [statusList]
    );

    const tickets = useMemo(() => (ticketList || []).map(toView), [ticketList]);

    /* a ticket reached by link is read from linkedTicket until the page the
       list is on happens to contain it */
    const selected =
        tickets.find((t) => t.key === selectedKey) ||
        (linkedTicket?.key === selectedKey ? linkedTicket : null) ||
        null;

    /* On the first render the list is still empty and `loading` has not flipped
       yet, so the wait is for a fetch to actually finish - giving up any earlier
       would drop the link before its ticket ever arrived. */
    useEffect(() => {
        if (loading) fetchSeen.current = true;
    }, [loading]);

    useEffect(() => {
        if (!pendingLink || loading || !fetchSeen.current) return;
        const match =
            tickets.find(
                (t) => pendingLink.id && String(t.id) === pendingLink.id
            ) ||
            tickets.find(
                (t) =>
                    pendingLink.no &&
                    String(t.no).replace(/^#/, "") === pendingLink.no
            );
        if (match) {
            setLinkedTicket(match);
            setSelectedKey(match.key);
            setViewing(true);
        }
        setPendingLink(null);
        /* the narrow fetch is done, so the full list is free to load */
        setFetchingLink(false);
        /* the link has done its job - the query leaves the url only now, so a
           remount before the list arrived can still read it and try again */
        setSearchParams({}, { replace: true });
    }, [tickets, loading, pendingLink]);

    useEffect(() => {
        if (
            selectedKey &&
            selectedKey !== linkedTicket?.key &&
            !tickets.some((t) => t.key === selectedKey)
        ) {
            setSelectedKey(null);
            setViewing(false);
        }
    }, [tickets]);

    useEffect(() => {
        if (selected?.id) {
            getVendorTicketConversation({ ticketId: selected.id });
        }
    }, [selected?.id]);

    const original = useMemo(() => {
        if (!selected) return null;
        return {
            who: selected.person,
            email: selected.email,
            dt: longStamp(selected.createdDate),
            body: selected.prev,
        };
    }, [selected]);

    const handlePick = (key) => {
        /* the user has chosen a row themselves - the linked ticket is spent */
        setLinkedTicket(null);
        setSelectedKey(key);
        setComposing(false);
        setHistoryOpen(false);
        setReplying(false);
        setPendingStatus("");
        setViewing(true);
    };

    const handleStatusChange = (option) => {
        if (!selected || !option || option.value === selected.status) return;
        setPendingStatus(option.value);
    };

    /* the status changes are read when the trail is opened - it is the only
       place they are shown, so there is no reason to fetch them earlier */
    useEffect(() => {
        if (historyOpen && selected?.id) {
            getVendorTicketComments({ ticketId: selected.id });
        }
    }, [historyOpen, selected?.id]);

    useEffect(() => {
        if (!updateResult) return;
        showToast(updateResult.message, updateResult.status ? SUCCESS : DANGER);
        if (updateResult.status) {
            setPendingStatus("");
            setReplying(false);
            /* the row, the header pill and the activity trail all read from the
               list, so it has to come back before any of them are right */
            loadTickets();
            if (selected?.id) getVendorTicketConversation({ ticketId: selected.id });
        }
    }, [updateResult?.at]);

    useEffect(() => {
        if (!shareResult) return;
        showToast(shareResult.message, shareResult.status ? SUCCESS : DANGER);
        /* the composer holds the recipient list, so closing it is what clears
           the draft after a successful forward */
        if (shareResult.status) {
            setComposing(false);
        }
    }, [shareResult?.at]);

    const pageCount = Math.max(1, totalPages || 1);

    return (
        <div className={`vp-tk-app${viewing ? " viewing" : ""}`}>
            <div className="vp-tk-list">
                <div className="vp-tk-tabs">
                    <span className="vp-tk-tabs-title">
                        Tickets
                        {totalRecords > 0 && (
                            <span className="vp-tk-count">{totalRecords}</span>
                        )}
                    </span>
                    <button
                        type="button"
                        className={`vp-tk-filter-btn${filterOpen ? " on" : ""}`}
                        onClick={() => setFilterOpen((open) => !open)}
                    >
                        <VendorFilterSvg />
                        Filter
                        {activeFilterCount > 0 && (
                            <span className="vp-tk-fcount">{activeFilterCount}</span>
                        )}
                        <VendorChevronDownSvg />
                    </button>
                </div>

                <div className="vp-tk-search">
                    <div className="vp-tk-sbox">
                        <VendorSearchSvg />
                        <input
                            type="text"
                            value={search}
                            placeholder="Search tickets — subject, name, ticket no."
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {filterOpen && (
                    <TicketFilters
                        hasMultipleVendors={hasMultipleVendors}
                        vendorOptions={vendorOptions}
                        storeOptions={storeOptions}
                        statusOptions={statusOptions}
                        vendorZohoId={vendorZohoId}
                        hraMemberId={hraMemberId}
                        status={status}
                        category={category}
                        priority={priority}
                        startDate={startDate}
                        endDate={endDate}
                        activeFilterCount={activeFilterCount}
                        onVendorChange={setVendorZohoId}
                        onStoreChange={setHraMemberId}
                        onStatusChange={setStatus}
                        onCategoryChange={setCategory}
                        onPriorityChange={setPriority}
                        onRangeChange={(start, end) => {
                            setStartDate(start);
                            setEndDate(end);
                        }}
                        onClear={clearFilters}
                    />
                )}

                <div className="vp-tk-msgs">
                    {loading ? (
                        <p className="vp-tk-none">Loading tickets…</p>
                    ) : tickets.length === 0 ? (
                        <p className="vp-tk-none">
                            {search || activeFilterCount > 0
                                ? "Nothing found. Try another filter."
                                : "No support tickets yet."}
                        </p>
                    ) : (
                        tickets.map((t) => (
                            <TicketRow
                                key={t.key}
                                ticket={t}
                                selected={t.key === selectedKey}
                                showVendor={hasMultipleVendors}
                                onPick={handlePick}
                            />
                        ))
                    )}
                </div>

                <TicketPager page={page} pageCount={pageCount} onChange={setPage} />
            </div>

            <div className="vp-tk-read">
                {!selected ? (
                    <>
                        <div className="vp-tk-empty">
                            <VendorEmptyMailboxSvg />
                            <b>Select an item to read</b>
                            <span>Nothing is selected</span>
                        </div>

                        {/* the shell footer is hidden on this page (the panes own
                            the viewport), so the empty pane carries the HRA mark
                            itself rather than ending on blank space */}
                        <div className="vp-tk-efoot">
                            <img
                                className="vp-foot-logo"
                                src={logoDark}
                                alt="HRA — The Power of Collective"
                            />
                            <span>© {new Date().getFullYear()} HRA. All rights reserved.</span>
                        </div>
                    </>
                ) : (
                    <>
                        <TicketDetailHead
                            ticket={selected}
                            statusOptions={statusOptions}
                            canUpdateTickets={canUpdateTickets}
                            hasMultipleVendors={hasMultipleVendors}
                            canShareTickets={canShareTickets}
                            onBack={() => setViewing(false)}
                            onOpenHistory={() => setHistoryOpen(true)}
                            onStatusChange={handleStatusChange}
                            onForward={() => setComposing(true)}
                            onReply={() => setReplying(true)}
                        />

                        <div className="vp-tk-rbody">
                            {composing && (
                                <ForwardCompose
                                    ticket={selected}
                                    original={original}
                                    hasMultipleVendors={hasMultipleVendors}
                                    sharing={sharing}
                                    showToast={showToast}
                                    onDiscard={() => setComposing(false)}
                                    onSend={(to) =>
                                        shareVendorTicket({
                                            ticketId: selected.id,
                                            to: to,
                                        })
                                    }
                                />
                            )}

                            {conversationLoading && (
                                <p className="vp-tk-nomsg">Loading replies…</p>
                            )}

                            {!conversationLoading &&
                                conversation.map((mail) => (
                                    <ConversationMessage
                                        key={mail.inboundId}
                                        mail={mail}
                                        avatarColor={selected.color}
                                    />
                                ))}

                            {!composing &&
                                !conversationLoading &&
                                conversation.length === 0 && (
                                    <p className="vp-tk-nomsg">
                                        No replies on this ticket yet.
                                    </p>
                                )}

                            {/* same mark as the empty pane carries - here it sits
                                at the end of the thread rather than pinned, so it
                                costs the conversation no height */}
                            <div className="vp-tk-efoot">
                                <img
                                    className="vp-foot-logo"
                                    src={logoDark}
                                    alt="HRA — The Power of Collective"
                                />
                                <span>© {new Date().getFullYear()} HRA. All rights reserved.</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ActivitiesModal
                isOpen={historyOpen && !!selected}
                ticket={selected}
                comments={ticketComments}
                loading={ticketCommentsLoading}
                onClose={() => setHistoryOpen(false)}
            />

            {/* mounted only while a reply/status change is open, so the comment
                and the picked files are gone for good once the modal is closed
                - a dialog that is always mounted would hand the next one the
                last one's half-finished draft. Reply and the header's status
                pill open the very same dialog - the only difference is what
                the status dropdown inside it starts on. */}
            {(replying || !!pendingStatus) && !!selected && (
                <CustomisedModal
                    modalView
                    scrollable
                    customClass="vp-modal vp-st-dialog"
                    modalName={
                        pendingStatus
                            ? `Update Status - #${selected.no}`
                            : `Reply - #${selected.no}`
                    }
                    onModalDismiss={() => {
                        setPendingStatus("");
                        setReplying(false);
                    }}
                >
                    <StatusChangeModal
                        ticket={selected}
                        toStatus={pendingStatus || selected.status}
                        statusOptions={statusOptions}
                        saving={updating}
                        onCancel={() => {
                            setPendingStatus("");
                            setReplying(false);
                        }}
                        onSubmit={(formData) => updateVendorTicket({ formData: formData })}
                    />
                </CustomisedModal>
            )}
        </div>
    );
};

const mapStateToProps = ({ VendorSupportTickets }) => {
    return {
        ticketList: VendorSupportTickets?.list || [],
        vendors: VendorSupportTickets?.vendors || [],
        totalRecords: VendorSupportTickets?.totalRecords || 0,
        totalPages: VendorSupportTickets?.totalPages || 0,
        statusList: VendorSupportTickets?.statusList || [],
        storeList: VendorSupportTickets?.storeList || [],
        loading: VendorSupportTickets?.loading || false,
        conversation: VendorSupportTickets?.conversation || [],
        conversationLoading: VendorSupportTickets?.conversationLoading || false,
        ticketComments: VendorSupportTickets?.ticketComments || [],
        ticketCommentsLoading: VendorSupportTickets?.ticketCommentsLoading || false,
        sharing: VendorSupportTickets?.sharing || false,
        shareResult: VendorSupportTickets?.shareResult || null,
        updating: VendorSupportTickets?.updating || false,
        updateResult: VendorSupportTickets?.updateResult || null,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVendorSupportTickets: (data) =>
            dispatch(getVendorSupportTicketsRequest(data)),
        getVendorTicketStatus: () => dispatch(getVendorTicketStatusRequest()),
        getVendorTicketStores: () => dispatch(getVendorTicketStoresRequest()),
        getVendorTicketConversation: (data) =>
            dispatch(getVendorTicketConversationRequest(data)),
        getVendorTicketComments: (data) =>
            dispatch(getVendorTicketCommentsRequest(data)),
        shareVendorTicket: (data) => dispatch(shareVendorTicketRequest(data)),
        updateVendorTicket: (data) => dispatch(updateVendorTicketRequest(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorSupportTickets);
